import React, { useEffect, useState, useRef, memo, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PrintIcon from '@mui/icons-material/Print';
import { Skeleton } from 'antd';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import CircleIcon from '@mui/icons-material/Circle';
import styled from 'styled-components';
import { Rnd } from 'react-rnd';
import CloseIcon from '@mui/icons-material/Close';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";
import { useCurrentUser } from 'store/users/selectors';
import { useReactToPrint } from 'react-to-print';
import { useMobileDetect, useTabletDetection } from 'containers/chat/hooks';
import { addPrintableClass, removePrintableClass } from 'utils/methods/textMethod';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { useNavigate } from 'react-router-dom';
import { usePrintContentPDF } from 'components/hooks/usePrintContentPDF';

const ChartWrap = styled.div`
  width: 100%;
  // height:30vh;
  aspect-ratio: 4 / 3;

  @media (max-width: 900px) { aspect-ratio: 3 / 4; min-height: 300px; }
  @media (max-width: 600px) { aspect-ratio: 1 / 1.1; max-height: 320px; }

  @media print {
    width: 100% !important;
    max-width: 100% !important;
    aspect-ratio: auto !important;
    height: 260px !important;
    max-height: 260px !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    overflow: visible !important;
    
    & svg text {
      opacity: 1 !important;
      visibility: visible !important;
      display: block !important;
    }
    & .recharts-tooltip-cursor,
    & .recharts-tooltip-cursor-fill,
    & .recharts-active-shape,
    & .recharts-reference-line,
    & .recharts-tooltip-wrapper {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
    }

    & svg {
      shape-rendering: crispEdges;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & .recharts-bar-rectangle {
      fill-opacity: 1 !important;
      stroke: none !important;
    }

    & rect[fill="rgba(0,0,0,0.06)"],
    & rect[fill="rgba(0,0,0,0)"] {
      display: none !important;
      visibility: hidden !important;
    }

    & text {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      opacity: 1 !important;
      visibility: visible !important;
    }
  }
`;

const CloseButtonWrapper = styled.div`
  position: relative;
  z-index: 1;
  cursor: pointer;
  background-color: red;
  border-radius: 100%;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media print {
    display: none !important;
  }
`;

const PrintButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: #fff;
  background-color: #000;
  width: 25px;
  height: 25px;
  border-radius: 100%;
  cursor: pointer;

  @media print {
    display: none !important;
  }
`;

const ViewReportButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: #fff;
  background-color: #3871c1;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  gap: 4px;

  &:hover {
    background-color: #2a5a9f;
  }

  @media print {
    display: none !important;
  }
`;

const PrintContent = styled.div`
  background-color: #f6f6f6;
  border-radius: 13px;
  border: 1px solid #000;
  padding: 20px;
  max-width: 80vw;
  position: relative;

  ${({ $isMobile }) =>
    $isMobile &&
    `
    max-width: 100%;
    width: 100%;
    height: 100%;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `}

  @media print {
    & svg text {
      opacity: 1 !important;
      visibility: visible !important;
      display: block !important;
    }
    & text {
      opacity: 1 !important;
      visibility: visible !important;
    }
    position: relative !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 15px !important;
    background-color: white !important;
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    overflow: visible !important;
    box-sizing: border-box !important;
    page-break-before: auto !important;
    break-before: auto !important;
    margin-top: 0 !important;
    padding-top: 0 !important;

    & > div:first-child {
      margin-top: 0 !important;
      padding-top: 0 !important;
      page-break-before: auto !important;
      break-before: auto !important;
    }

    & > div {
      overflow: visible !important;
    }

    & .recharts-tooltip-cursor,
    & .recharts-tooltip-cursor-fill,
    & .recharts-active-shape,
    & .recharts-reference-line,
    & .recharts-tooltip-wrapper,
    & rect[fill="rgba(0,0,0,0.06)"],
    & rect[fill="rgba(0,0,0,0)"] {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
    }

    & svg {
      shape-rendering: crispEdges;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & .recharts-bar-rectangle {
      fill-opacity: 1 !important;
      stroke: none !important;
    }

    & p {
      margin: 5px 0 !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & progress {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & ul {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    & .pill-progress {
      width: 100% !important;
      height: 20px !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & .pill-progress-fill {
      background: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & svg.MuiSvgIcon-root {
      display: none !important;
    }

    & .print-header-section svg.MuiSvgIcon-root,
    & ul svg.MuiSvgIcon-root {
      display: inline-block !important;
      visibility: visible !important;
    }


    & .print-header-section {
      margin-top: 0 !important;
      margin-bottom: 10px !important;
      padding-top: 0 !important;
      padding-bottom: 10px !important;
      border-bottom: 1px solid #ddd !important;
      page-break-before: auto !important;
      break-before: auto !important;
    }

    & .print-header-section p {
      color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & div[style*="background"] {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & div[style*="backgroundImage"] {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    & .ant-skeleton {
      display: none !important;
    }

    @page {
      size: letter;
      margin: 15mm;
    }
  }
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-start;
  border-bottom: 1px solid #8a8a8a;
  margin-top: 5px;

  ${({ $isMobile }) =>
    $isMobile &&
    `
    flex-direction: column;
    align-items: stretch;
    border-bottom: none;

    & > div {
      flex-basis: 100% !important;
      width: 100% !important;
      max-width: 100% !important;
    }
  `}

  @media print {
    flex-direction: column !important;
    align-items: stretch !important;
    border-bottom: none !important;
    margin-top: 10px !important;
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
    gap: 0 !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;

    & > div {
      flex-basis: 100% !important;
      width: 100% !important;
      max-width: 100% !important;
      height: auto !important;
      min-height: 0 !important;
      margin-bottom: 0 !important;
    }

    & .print-left-section {
      border-bottom: 1px solid #ddd !important;
      padding-bottom: 10px !important;
      margin-bottom: 10px !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      width: 100% !important;
    }

    & .print-right-section {
      border-bottom: none !important;
      padding-bottom: 0 !important;
      width: 100% !important;
      height: auto !important;
      min-height: 0 !important;
      max-height: none !important;
      overflow: visible !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
  }
`;

const ScoreDistributionChart = memo(({ data }) => {
  const xAxisTick = useMemo(() => ({ x, y, payload }) => {
    const parts = String(payload.value).split(' ');
    const first = parts[0] ?? '';
    const rest = parts.slice(1).join(' ');
    return (
      <g transform={`translate(${x},${y})`}>
        <text dy={15} textAnchor="middle" fill="#374201" fontSize="10px" style={{ fontSize: '8px' }}>
          <tspan x="0" style={{ fontSize: '10px' }}>{first}</tspan>
          {rest && <tspan x="0" dy="18" fontWeight="normal" style={{ fontSize: '10px' }}>{rest}</tspan>}
        </text>
      </g>
    );
  }, []);

  return (
    <ChartWrap className="print-pdf-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 0, right: 0, bottom: 45, left: 25 }}
          barCategoryGap="5%" 
          barGap={0}
          isAnimationActive={false}
        >
          <CartesianGrid strokeLinecap="3 3" opacity={1} vertical={false} horizontal={true} />
          <XAxis
            dataKey="name"
            interval={0}
            height={55}
            axisLine={false}
            tickMargin={10}
            tickLine={false}
            tick={xAxisTick}
          />
          <YAxis
            axisLine={false}
            allowDecimals={false}
            tickLine={false}
            width={35}
            tickMargin={5}
            tick={{ fill: "#61677E", fontSize: 12, style: { fontSize: '12px' } }}
          />
          <Tooltip cursor={{ fill: "rgba(0,0,0,0.06)" }} />
          <Bar
            dataKey="value"
            fill="#716dc2"
            radius={[8, 8, 0, 0]}
            barSize={100}
            maxBarSize={100}
          >
            <LabelList 
              dataKey="value" 
              position="insideTop" 
              fill="#000"
              fontSize={12}
              fontWeight={600}
              stroke="#000"
              strokeWidth={0.3}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrap>
  );
}, (prevProps, nextProps) => {
  if (!prevProps.data || !nextProps.data) return prevProps.data === nextProps.data;
  if (prevProps.data.length !== nextProps.data.length) return false;
  return prevProps.data.every((item, index) => {
    const nextItem = nextProps.data[index];
    return item.name === nextItem.name && item.value === nextItem.value;
  });
});

ScoreDistributionChart.displayName = 'ScoreDistributionChart';

const DomainsPerformanceChart = memo(({ data, color }) => {
  const tooltipFormatter = useMemo(() => (value, name) => [`${value}%`, name], []);
  const labelFormatter = useMemo(() => (label) => label, []);
  const gradientId = useMemo(() => `domains-gradient-${Math.random().toString(36).slice(2, 10)}`, []);
  const gradientStops = useMemo(() => {
    if (typeof color !== 'string') return null;
    const match = color.match(/linear-gradient\(\s*180deg\s*,\s*([^,]+)\s*,\s*([^)]+)\)/i);
    if (!match) return null;
    return {
      start: match[1].trim(),
      end: match[2].trim(),
    };
  }, [color]);
  const barFill = gradientStops ? `url(#${gradientId})` : (color || "#fb742b");

  return (
    <ChartWrap className="print-pdf-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 20, right: 8, bottom: 4, left: 8 }}
          barSize={56}
          maxBarSize={64}
          barCategoryGap={8}
          barGap={4}
          isAnimationActive={false}
        >
          {gradientStops && (
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={gradientStops.start} />
                <stop offset="100%" stopColor={gradientStops.end} />
              </linearGradient>
            </defs>
          )}
          <CartesianGrid strokeA="3 3" vertical horizontal={false} />
          <Legend
            verticalAlign="top"
            align="center"
            iconType="circle"
            iconSize={10}
            wrapperStyle={{ paddingBottom: 8 }}
          />
          <XAxis
            type="number"
            dataKey="value"
            tick={{ fill: "#61677E", fontSize: 12, style: { fontSize: '12px' } }}
            tickMargin={6}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={90}
            tickMargin={26}
            tick={{ fill: "#61677E", fontSize: 12, style: { fontSize: '12px' } }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip 
            cursor={false}
            formatter={tooltipFormatter}
            labelFormatter={labelFormatter}
          />
          <Bar
            name="Domains"
            dataKey="value"
            fill={barFill}
            barSize={90}
            radius={[0, 10, 10, 0]}
            fillOpacity={1}
            activeBar={{ style: { fillOpacity: 0.8 } }}
          >
            <LabelList 
              dataKey="value" 
              position="insideRight" 
              formatter={(value) => `${value}%`}
              fill="#000"
              fontSize={12}
              fontWeight={600}
              stroke="#000"
              strokeWidth={0.3}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrap>
  );
}, (prevProps, nextProps) => {
  if (!prevProps.data || !nextProps.data) return prevProps.data === nextProps.data;
  if (prevProps.data.length !== nextProps.data.length) return false;
  return prevProps.data.every((item, index) => {
    const nextItem = nextProps.data[index];
    return item.name === nextItem.name && item.value === nextItem.value;
  });
});

DomainsPerformanceChart.displayName = 'DomainsPerformanceChart';

const MOBILE_BOTTOM_NAV_OFFSET_FALLBACK = 58;

const getMobileBottomNavOffset = () => {
  if (typeof document === 'undefined') return MOBILE_BOTTOM_NAV_OFFSET_FALLBACK;
  const footer = document.getElementById('bottomContnrRemove');
  if (!footer) return MOBILE_BOTTOM_NAV_OFFSET_FALLBACK;
  const height = footer.offsetHeight || footer.getBoundingClientRect().height;
  return height > 0 ? Math.ceil(height) : 0;
};

const getProficiencyExamModalRndDefaults = (isMobileViewport, footerOffset = 0) => {
  if (typeof window === 'undefined') {
    return { x: 12, y: 64, width: 340, height: 500 };
  }
  const padding = 12;
  const viewportHeight = isMobileViewport
    ? window.innerHeight - footerOffset
    : window.innerHeight;
  const modalWidth = isMobileViewport
    ? window.innerWidth - padding * 2
    : Math.min(Math.max(window.innerWidth * 0.55, 520), 900);
  const modalHeight = isMobileViewport
    ? Math.round(viewportHeight * 0.72)
    : Math.min(Math.round(window.innerHeight * 0.85), 700);
  return {
    x: Math.max(padding, Math.round((window.innerWidth - modalWidth) / 2)),
    y: Math.max(isMobileViewport ? 48 : 64, Math.round((viewportHeight - modalHeight) / 2)),
    width: modalWidth,
    height: modalHeight,
  };
};

const RndWrapper = styled.div`
  ${({ $isMobile, $footerOffset = 0 }) =>
    $isMobile &&
    `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: ${$footerOffset}px;
    z-index: 1000;
    pointer-events: none;
  `}

  @media print {
    position: static !important;
    transform: none !important;
    width: 100% !important;
    height: auto !important;
    z-index: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    page-break-before: auto !important;
    break-before: auto !important;
    
    & > div {
      position: static !important;
      transform: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  }
`;

const MobileModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: ${({ $footerOffset = 0 }) => $footerOffset}px;
  background: rgba(0, 0, 0, 0.45);
  pointer-events: auto;
`;

const MobileModalScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ModalWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media print {
    position: static !important;
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    page-break-before: auto !important;
    break-before: auto !important;
  }
`;

const ProficiencyExamDragHeaderZone = styled.div`
  flex-shrink: 0;
  box-sizing: border-box;
  width: calc(100% + 40px);
  margin: -20px -20px 0 -20px;
  padding: 20px 20px 8px 20px;
  cursor: grab;
  user-select: none;
  touch-action: none;
  -webkit-user-select: none;
  background: #f6f6f6;
  border-radius: 12px 12px 0 0;

  &:active {
    cursor: grabbing;
  }

  ${({ $isMobile }) =>
    $isMobile &&
    `
    width: calc(100% + 32px);
    margin: -16px -16px 0 -16px;
    padding: 16px 16px 8px 16px;
  `}

  @media print {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    cursor: default !important;
    border-radius: 0 !important;
    background: transparent !important;
  }
`;

const PrintHeaderDragRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const ProficiencyExamEyePopUpModal = ({ teacher, toggleModalOpen, modalOpen, data, openFlagModalOpen, dispatch, handleGetTeacherSubmissionDetailsProficiency, session, selectedClassId }) => {
  const [loading, setLoading] = useState(false);
  const [teacherData, setTeacherData] = useState(null);
  const [overallPerformance, setOverallPerformance] = useState(0);
  const [domains, setDomains] = useState(data || []);
  const [scoreDistribution, setScoreDistribution] = useState([]);
  const currentUser = useCurrentUser();
  const [districtOverallPerformance, setDistrictOverallPerformance] = useState(0);
  const printRef = useRef();
  const { handleDownloadPDF, isExporting } = usePrintContentPDF(printRef, {
    fileName: 'proficiency-exam-report.pdf',
    orientation: 'portrait',
  });
  const [printLoader, setPrintLoader] = useState(false);
  const isTablate = useTabletDetection();
  const isMobile = useMobileDetect() && !isTablate;
  const showAlert = useAlert();
  const navigate = useNavigate();
  const [mobileFooterOffset, setMobileFooterOffset] = useState(MOBILE_BOTTOM_NAV_OFFSET_FALLBACK);
  const rndDefaults = useMemo(
    () => getProficiencyExamModalRndDefaults(isMobile, mobileFooterOffset),
    [isMobile, mobileFooterOffset]
  );

  useEffect(() => {
    if (!isMobile || !modalOpen) return undefined;
    const updateFooterOffset = () => setMobileFooterOffset(getMobileBottomNavOffset());
    updateFooterOffset();
    window.addEventListener('resize', updateFooterOffset);
    window.addEventListener('orientationchange', updateFooterOffset);
    return () => {
      window.removeEventListener('resize', updateFooterOffset);
      window.removeEventListener('orientationchange', updateFooterOffset);
    };
  }, [isMobile, modalOpen]);

  useEffect(() => {
    const load = async () => {
      if (!teacher?.teacherId || !dispatch || !handleGetTeacherSubmissionDetailsProficiency) return;
      setLoading(true);
      try {
        const payload = {
          teacher_id: teacher.teacherId,
          session: session,
          orgId: currentUser.orgId
        }
        if (currentUser?.role == 'teacher') {
          payload.student_id = teacher?.studentId;
          if (selectedClassId) {
            payload.class_id = selectedClassId;
          }
        }
        const res = await dispatch(handleGetTeacherSubmissionDetailsProficiency(payload));
        if (res?.success && res?.data) {
          if (Array.isArray(res.data.domains)) setDomains(res.data.domains);
          if (Array.isArray(res.data.scoreDistribution)) setScoreDistribution(res.data.scoreDistribution);
          if (typeof res.data.overallPerformance === 'number') setOverallPerformance(res.data.overallPerformance);
          if (res.data.studentData) setTeacherData(res.data.studentData);
          if (typeof res.data.districtOverallPerformance === 'number') setDistrictOverallPerformance(res.data.districtOverallPerformance);
        }
      } finally {
        setLoading(false);
      }
    };
    if (modalOpen) load();
  }, [modalOpen, teacher, dispatch, handleGetTeacherSubmissionDetailsProficiency, session, currentUser.orgId, currentUser?.role, selectedClassId]);

  const flagDataProficiencyExam = [
    {
      title: 'Red = Urgent Support (high 50% failing, downward trend).',
      backgroundcolor: 'linear-gradient(180deg,#ef476f,#d7265e)',
    },
    {
      title: 'Orange = Needs Attention (moderate 25% failing, stagnant growth).',
      backgroundcolor: 'linear-gradient(180deg, #FFB703, #FB8500)',
    },
    {
      title: 'Green = On Track (80% passing  majority, positive trend).',
      backgroundcolor: 'linear-gradient(180deg, #03989e, #03989e)',
    },
  ];
  const getPriorityColor = (performance) => {
    if (performance >= 80) return flagDataProficiencyExam?flagDataProficiencyExam[2].backgroundcolor : '#078247';
    if (performance >= 60) return flagDataProficiencyExam?flagDataProficiencyExam[1].backgroundcolor : '#7b13c3';
    return flagDataProficiencyExam?flagDataProficiencyExam[0].backgroundcolor : '#df0d14';
  };

  const getPriorityText = (performance) => {
    if (performance >= 80) return 'On Track';
    if (performance >= 60) return 'Needs Attention';
    return 'Urgent Support Needed';
  };

  const printCall = useReactToPrint({
    contentRef: printRef,
    onBeforeGetContent: async () => {
      return new Promise((resolve) => {
        setPrintLoader(true);
        setTimeout(() => resolve(), 100);
      });
    },
    onBeforePrint: async () => {
      setPrintLoader(true);
    },
    onAfterPrint: async () => {
      setPrintLoader(false);
      removePrintableClass();
    },
    onPrintError: async () => {
      setPrintLoader(false);
      removePrintableClass();
    },
  });

  const handlePrint = (e) => {
    if (isMobile || isTablate) {
      handleDownloadPDF(e);
      return;
    }
    // if (isMobile || isTablate) {
    //   showAlert("Print is not supported on this device!", "error");
    //   return;
    // }
    setPrintLoader(true);
    addPrintableClass();
    setTimeout(() => {
      printCall();
    }, 100);
  };

  const handleViewReport = () => {
    if (teacher?.teacherId && teacher?.orgId) {
      navigate(`/proficiency?from=teacher&orgId=${teacher.orgId}&teacherId=${teacher.teacherId}`);
    }
  };

  const priorityBadgeRowStyle = isMobile
    ? { display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '8px', width: '100%' }
    : { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '10px' };

  const renderReportContent = () => (
    <ContentRow $isMobile={isMobile}>
      <div style={{ flexBasis: '50%', flexShrink: 0, flexGrow: 0 }} className="print-left-section">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', gap: '5px', width: '100%' }}>
          <p style={{ fontSize: '14px', fontWeight: '600', margin: '0' }}>Overall Performance</p>
          {loading ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          ) : (
            <>
              <progress className="pill-progress" id="file" value={overallPerformance} max="100">
                {overallPerformance}%
              </progress>
              <p style={{ fontSize: '12px', fontWeight: '400', margin: '0px' }}>
                {teacherData?.averageGrade || overallPerformance}% average grade from {teacherData?.totalSubmissions || 0} submissions.
              </p>
            </>
          )}
          {loading ? (
            <div style={priorityBadgeRowStyle}>
              <Skeleton.Input active size="small" style={{ width: '140px', height: '24px', borderRadius: '38px' }} />
              <Skeleton.Input active size="small" style={{ width: '220px', height: '24px', borderRadius: '38px' }} />
            </div>
          ) : (
            <div style={priorityBadgeRowStyle}>
              <div
                className="proficiency-exam-flag-trigger"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: getPriorityColor(overallPerformance),
                  backgroundImage: getPriorityColor(overallPerformance),
                  padding: '2px 10px',
                  borderRadius: '38px',
                }}
              >
                <p style={{ fontSize: '12px', color: '#fff', lineHeight: '1.2', padding: '2px 4px', margin: '0' }}>
                  {getPriorityText(overallPerformance)}
                </p>
                <InfoOutlineIcon
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    openFlagModalOpen();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openFlagModalOpen();
                  }}
                  sx={{ color: '#000', width: '20px', height: '20px', cursor: 'pointer' }}
                />
              </div>
              <div
                style={{
                  border: '1px solid #000',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fff',
                  padding: '2px 10px',
                  borderRadius: '38px',
                }}
              >
                <p style={{ fontSize: '12px', color: '#000', lineHeight: '1.2', padding: '2px 4px', margin: '0' }}>
                  Compared to District Avg:{districtOverallPerformance}%
                </p>
              </div>
            </div>
          )}
          <p style={{ fontSize: '14px', fontWeight: '600', margin: '0' }}>Score Distribution</p>
          {loading ? (
            <Skeleton active paragraph={{ rows: 9 }} title={false} style={{ width: '100%', marginTop: 8 }} />
          ) : (
            <>
              <ul
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  gap: '5px',
                  padding: isMobile ? '0' : '0px 30px',
                  flexWrap: 'wrap',
                  margin: 0,
                }}
              >
                {(scoreDistribution || []).map((item, index) => (
                  <p
                    style={{
                      fontSize: '12px',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      margin: '0',
                    }}
                    key={index}
                  >
                    <CircleIcon sx={{ width: '10px', height: '10px', color: '#3871c1' }} /> {item.name}
                  </p>
                ))}
              </ul>
              <ScoreDistributionChart data={scoreDistribution} />
            </>
          )}
        </div>
      </div>
      <div
        style={{ flexBasis: '50%', flexShrink: 0, flexGrow: 0, width: '100%', height: '100%', alignSelf: 'flex-start' }}
        className="print-right-section"
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '5px',
            width: '100%',
            height: '100%',
          }}
        >
          <p style={{ fontSize: '14px', fontWeight: '600', margin: '0' }}>Domains Performance</p>
          {loading ? (
            <Skeleton active paragraph={{ rows: 10 }} title={false} style={{ width: '100%' }} />
          ) : (
            <DomainsPerformanceChart data={domains} color={getPriorityColor(overallPerformance)} />
          )}
        </div>
      </div>
    </ContentRow>
  );

  const renderModalInner = () => (
    <>
      <PrintContent ref={printRef} $isMobile={isMobile} className="121212 to_be_print">
        <ProficiencyExamDragHeaderZone
          $isMobile={isMobile}
          className="proficiency-exam-drag-handle"
        >
          <PrintHeaderDragRow className="print-header-section">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', lineHeight: '1.2', minWidth: 0, flex: 1 }}>
              {loading ? (
                <Skeleton active paragraph={{ rows: 1 }} title={false} />
              ) : (
                <>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#252525', margin: '0px' }}>
                    {teacher?.title || `${teacher?.firstName || ''} ${teacher?.lastName || ''}`}{' '}
                    {teacher?.grade ? `– ${teacher.grade} Grade` : ''} (
                    {currentUser?.role == 'teacher' ? 'Student' : 'Teacher'})
                  </p>
                  <p style={{ fontSize: '12px', fontWeight: '400', color: '#9CA3AF', margin: '0px' }}>
                    {teacher?.school || 'N/A'}
                  </p>
                </>
              )}
            </div>
            <div
              className="proficiency-exam-header-actions"
              style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', flexShrink: 0 }}
            >
              {currentUser?.role != 'teacher' && (
                <ViewReportButton onTouchStart={handleViewReport} onClick={handleViewReport}>
                  View Report
                </ViewReportButton>
              )}
              <PrintButtonWrapper onTouchStart={handlePrint} onClick={handlePrint}>
                <PrintIcon sx={{ width: '18px', height: '18px' }} />
              </PrintButtonWrapper>
              <CloseButtonWrapper
                className="proficiency-exam-close-wrap"
                onTouchStart={toggleModalOpen}
                onClick={toggleModalOpen}
              >
                <CloseIcon style={{ width: 20, height: 20, color: 'white' }} />
              </CloseButtonWrapper>
            </div>
          </PrintHeaderDragRow>
        </ProficiencyExamDragHeaderZone>
        {isMobile ? <MobileModalScrollBody>{renderReportContent()}</MobileModalScrollBody> : renderReportContent()}
      </PrintContent>
    </>
  );

  const modalNode = (
    <RndWrapper className="ProficyExamModalUpr" $isMobile={isMobile} $footerOffset={mobileFooterOffset}>
      {isMobile && <MobileModalBackdrop $footerOffset={mobileFooterOffset} onClick={toggleModalOpen} />}
      <Rnd
        default={
          isMobile
            ? rndDefaults
            : {
                x: isTablate ? 40 : -300,
                y: -200,
              }
        }
        size={{ width: rndDefaults.width, height: rndDefaults.height }}
        bounds={isMobile ? 'parent' : 'window'}
        enableResizing={false}
        dragHandleClassName="proficiency-exam-drag-handle"
        cancel=".proficiency-exam-close-wrap,.proficiency-exam-close-wrap *,.proficiency-exam-header-actions,.proficiency-exam-header-actions *,.proficiency-exam-flag-trigger,.proficiency-exam-flag-trigger *,button"
        style={{ zIndex: isMobile ? 1001 : 1000, pointerEvents: 'auto' }}
      >
        <ModalWrapper className="ProficyExamModal">{renderModalInner()}</ModalWrapper>
      </Rnd>
    </RndWrapper>
  );

  if (isMobile && typeof document !== 'undefined') {
    return createPortal(modalNode, document.body);
  }

  return modalNode;
}

export default ProficiencyExamEyePopUpModal

